import { useEffect, useMemo, useRef, useState, type JSX } from 'react';

import {
  GUIDE_CATEGORIES,
  GUIDE_CATEGORY_LABELS_FA,
  Permission,
  toPersianDigits,
  type GuideCategory,
} from '@bookora/shared';
import {
  Button,
  Checkbox,
  Collapse,
  DisabledReason,
  EmptyState,
  Icon,
  JalaliDate,
  LoadingBlock,
  QueryError,
  Radio,
  RelativeTime,
  StatusTag,
  Switch,
  Typography,
  useConfirm,
  useReducedMotion,
} from '@bookora/ui';

import type {
  GuideTipResponse,
  GuideTipsResponse,
  UpdateGuideClinicSettingsDto,
} from '@/api/generated/model';
import { usePlanLock } from '@/features/plan/plan-access';
import { useGuidance } from '@/guidance';
import { useCan } from '@/permissions/use-can';

import {
  useGuideClinicSettingsQuery,
  useGuideOccasionsQuery,
  useGuideTipsQuery,
  useUpdateGuideClinicSettings,
  useUpdateTipPreferences,
  useVerifyTipsOnMount,
} from './api';
import { HUB } from './copy';
import { GuideTipCard } from './guide-tip-card';
import { GuideUndoRow } from './guide-undo-row';
import { useTipImpressions } from './impressions';
import { SeenTip } from './seen-tip';
import { tipTitleText } from './tip-model';
import { useClinicReadOnly } from './use-clinic-read-only';
import { useGuideCta } from './use-guide-cta';
import { useTipActions } from './use-tip-actions';
import './guide.css';

/** How long a deep-linked tip keeps its ring. */
const FOCUS_RING_MS = 2_000;
/** The occasion list's horizon. */
const OCCASION_DAYS = 60;

type TipsMode = 'on' | 'day' | 'week' | 'off';
/** Rule keys as the clinic-settings DTO types them (the catalogue's keys). */
type HiddenRules = NonNullable<UpdateGuideClinicSettingsDto['hiddenRules']>;

/**
 * «پیشنهادها» inside the «راهنما و پیشنهادها» hub — the one place every
 * suggestion is listed, whatever the dashboard is allowed to show.
 *
 * Top to bottom: why the dashboard is quiet (if it is), the current tips with
 * their full reasons, upcoming occasions for whoever sends bulk SMS, what was
 * put off («بعداً») or set aside («کنار گذاشته‌ها») with a way back, what was
 * done, and the person's own settings — plus, for settings holders, the
 * clinic-wide switch and the rules hidden for everyone.
 *
 * Opening the hub re-checks every tip against the clinic's live data. A deep
 * link (`?help=tip:<itemKey>`, handled by the hub) arrives as `focusId`: that
 * tip is scrolled into view and ringed for two seconds.
 *
 * While a support session views the panel as this user, nothing here writes:
 * every control is blocked, with the reason.
 */
export function GuideHubSection({ focusId }: { focusId: string | null }): JSX.Element {
  const query = useGuideTipsQuery();
  useVerifyTipsOnMount(true);
  const data = query.data;

  if (query.isLoading) return <LoadingBlock rows={3} />;
  if (query.error || !data) {
    return <QueryError error={query.error} onRetry={() => void query.refetch()} />;
  }
  return <HubBody data={data} focusId={focusId} />;
}

function HubBody({
  data,
  focusId,
}: {
  data: GuideTipsResponse;
  focusId: string | null;
}): JSX.Element {
  const readOnly = data.readOnly;
  const actions = useTipActions('HUB');
  const runner = useGuideCta({
    surface: 'HUB',
    readOnly,
    onMarkDone: (tip) => actions.hide(tip, 'MARK_DONE'),
  });
  const seen = useTipImpressions({ source: 'hub', surface: 'HUB' });
  const canSendSms = useCan(Permission.CRM_SEND_CAMPAIGNS);
  // The occasion calendar is for planning group SMS: on a plan without it
  // there is nothing to act on, so no heading and no «مناسبتی نداریم» either
  // (a locked feature is advertised by the plan screens, not by the hub).
  // Fails open while plan access loads, like every panel lock.
  const groupSms = usePlanLock('campaigns').state === 'open';
  const listRef = useRef<HTMLDivElement>(null);
  const [ringed, setRinged] = useState<string | null>(null);
  const reduced = useReducedMotion();

  // The deep-linked tip: scrolled to, focused, ringed for two seconds.
  const present = focusId !== null && data.tips.some((tip) => tip.itemKey === focusId);
  useEffect(() => {
    if (!focusId || !present) return;
    const element = listRef.current?.querySelector<HTMLElement>(
      `[data-guide-tip="${CSS.escape(focusId)}"]`,
    );
    if (!element) return;
    setRinged(focusId);
    element.scrollIntoView?.({ block: 'center', behavior: reduced ? 'auto' : 'smooth' });
    element.focus({ preventScroll: true });
    const timer = setTimeout(() => setRinged(null), FOCUS_RING_MS);
    return () => clearTimeout(timer);
  }, [focusId, present, reduced]);

  return (
    <div className="bk-guide-hub">
      <QuietNotice data={data} />

      <section aria-labelledby="bk-guide-hub-now">
        <h5 className="bk-guide-hub__heading" id="bk-guide-hub-now">
          {HUB.now}
        </h5>
        {actions.undo ? (
          <GuideUndoRow
            key={actions.undo.id}
            message={actions.undo.message}
            onRestore={actions.undoLast}
            onExpire={actions.clearUndo}
          />
        ) : null}
        {data.tips.length === 0 ? (
          <EmptyState compact title={HUB.emptyTitle} description={HUB.emptyBody} />
        ) : (
          <div className="bk-guide-hub__list" ref={listRef}>
            {data.tips.map((tip, index) => (
              <SeenTip key={tip.itemKey} tip={tip} rank={index} onSeen={seen}>
                <GuideTipCard
                  tip={tip}
                  variant="row"
                  readOnly={readOnly}
                  runner={runner}
                  onHide={actions.hide}
                  focused={ringed === tip.itemKey}
                />
              </SeenTip>
            ))}
          </div>
        )}
      </section>

      {canSendSms && groupSms ? <OccasionsList /> : null}

      <PutAside data={data} readOnly={readOnly} onRestore={actions.restoreKey} />

      {data.done.length > 0 ? (
        <section aria-labelledby="bk-guide-hub-done">
          <h5 className="bk-guide-hub__heading" id="bk-guide-hub-done">
            {HUB.done}
          </h5>
          <div>
            {data.done.map((item) => (
              <div className="bk-guide-plain-row" key={item.itemKey}>
                <span className="bk-guide-plain-row__main">
                  <span>
                    <Icon name="check" size={16} className="bk-guide-done-icon" /> {item.title}
                  </span>
                </span>
                <span className="bk-guide-plain-row__meta">
                  <RelativeTime value={item.doneAt} />
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <TipSettings data={data} readOnly={readOnly} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Quiet notice                                                               */
/* -------------------------------------------------------------------------- */

function QuietNotice({ data }: { data: GuideTipsResponse }): JSX.Element | null {
  const preferences = useUpdateTipPreferences();
  const canEdit = useCan(Permission.SETTINGS_EDIT);
  const clinic = useUpdateGuideClinicSettings();
  const guidance = useGuidance();
  const clinicReadOnly = useClinicReadOnly();
  const reason = data.quietReason ?? null;

  // Billing read-only: the dashboard card and inline tips are gone and every
  // write CTA here is blocked — say why once, above the list. (The engine may
  // send it as its own quiet reason too; either way the words are these.)
  if (clinicReadOnly || (reason as string | null) === 'CLINIC_READ_ONLY') {
    return (
      <Typography.Paragraph className="bk-muted">{HUB.quiet.CLINIC_READ_ONLY}</Typography.Paragraph>
    );
  }
  if (!reason) return null;

  if (reason === 'BACKOFF') {
    // Two «×» in a row made the dashboard quieter; say so, and offer the way
    // back — the resume is the profile's `tipsPauseDays: 0`, which the ONE
    // writer (GuidanceService) folds into «the streak and its quiet end».
    const until = guidance.data?.profile.tipBackoffUntil ?? null;
    return (
      <div className="bk-guide-plain-row">
        <span>
          {until ? (
            <>
              {HUB.quiet.BACKOFF_LEAD}{' '}
              <JalaliDate value={until} variant="long" withRelative={false} />{' '}
              {HUB.quiet.BACKOFF_TAIL}
            </>
          ) : (
            HUB.quiet.BACKOFF
          )}
        </span>
        <Button
          size="small"
          loading={preferences.isPending}
          disabledReason={data.readOnly ? HUB.readOnlyHelper : null}
          onClick={() => preferences.mutate({ tipsPauseDays: 0 })}
        >
          {HUB.resume}
        </Button>
      </div>
    );
  }

  if (reason === 'FIRST_SESSION' || reason === 'WELCOME_DAY') {
    return (
      <Typography.Paragraph className="bk-muted">{HUB.quiet.FIRST_SESSION}</Typography.Paragraph>
    );
  }
  if (reason === 'OFF') {
    return <Typography.Paragraph className="bk-muted">{HUB.quiet.OFF}</Typography.Paragraph>;
  }
  if (reason === 'PAUSED') {
    return (
      <div className="bk-guide-plain-row">
        <span>
          {HUB.quiet.PAUSED_LEAD}{' '}
          <JalaliDate value={data.pausedUntil ?? null} variant="long" withRelative={false} />{' '}
          {HUB.quiet.PAUSED_TAIL}
        </span>
        <Button
          size="small"
          loading={preferences.isPending}
          disabledReason={data.readOnly ? HUB.readOnlyHelper : null}
          onClick={() => preferences.mutate({ tipsEnabled: true, tipsPauseDays: 0 })}
        >
          {HUB.resume}
        </Button>
      </div>
    );
  }
  if (reason === 'CLINIC_DISABLED') {
    return (
      <div className="bk-guide-plain-row">
        <span>{HUB.quiet.CLINIC_DISABLED}</span>
        {canEdit ? (
          <DisabledReason reason={data.readOnly ? HUB.readOnlyHelper : null}>
            <Switch
              checked={false}
              loading={clinic.isPending}
              disabled={data.readOnly}
              aria-label={HUB.clinicSwitch}
              onChange={(next) => clinic.mutate({ enabled: next })}
            />
          </DisabledReason>
        ) : null}
      </div>
    );
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Occasions                                                                  */
/* -------------------------------------------------------------------------- */

function OccasionsList(): JSX.Element | null {
  const query = useGuideOccasionsQuery(OCCASION_DAYS, true);
  if (query.isLoading) return <LoadingBlock rows={2} />;
  if (!query.data) return null;
  const occasions = query.data.occasions;
  return (
    <section aria-labelledby="bk-guide-hub-occasions">
      <h5 className="bk-guide-hub__heading" id="bk-guide-hub-occasions">
        {HUB.occasions}
      </h5>
      {occasions.length === 0 ? (
        <Typography.Text className="bk-muted">{HUB.occasionsEmpty}</Typography.Text>
      ) : (
        <div>
          {occasions.map((occasion) => (
            <div className="bk-guide-plain-row" key={`${occasion.key}-${occasion.date}`}>
              <span className="bk-guide-plain-row__main">
                <span>{occasion.nameFa}</span>
                <span className="bk-guide-plain-row__meta">
                  <JalaliDate value={occasion.date} variant="long" withRelative={false} />
                  {' · '}
                  {occasion.daysLeft <= 0
                    ? HUB.today
                    : HUB.daysLeft(toPersianDigits(occasion.daysLeft))}
                </span>
              </span>
              <span className="bk-guide-tags">
                {occasion.officialHoliday ? (
                  <StatusTag tone="inert">{HUB.tagHoliday}</StatusTag>
                ) : null}
                {occasion.promo === 'HARD' ? (
                  <StatusTag tone="warning">{HUB.tagHard}</StatusTag>
                ) : occasion.promo === 'SOFT' ? (
                  <StatusTag tone="progress">{HUB.tagSoft}</StatusTag>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* «بعداً» and «کنار گذاشته‌ها»                                                */
/* -------------------------------------------------------------------------- */

function PutAside({
  data,
  readOnly,
  onRestore,
}: {
  data: GuideTipsResponse;
  readOnly: boolean;
  onRestore: (itemKey: string) => void;
}): JSX.Element | null {
  const reason = readOnly ? HUB.readOnlyHelper : null;
  const items = [
    data.later.length > 0 && {
      key: 'later',
      label: `${HUB.later} — ${HUB.laterSummary(toPersianDigits(data.later.length))}`,
      children: (
        <div>
          {data.later.map((tip) => (
            <div className="bk-guide-plain-row" key={tip.itemKey}>
              <span className="bk-guide-plain-row__main">
                <span>{tipTitleText(tip)}</span>
                {tip.snoozedUntil ? (
                  <span className="bk-guide-plain-row__meta">
                    <JalaliDate value={tip.snoozedUntil} variant="long" withRelative={false} />
                  </span>
                ) : null}
              </span>
              <Button
                size="small"
                type="link"
                className="bk-guide-link"
                disabledReason={reason}
                onClick={() => onRestore(tip.itemKey)}
              >
                {HUB.showNow}
              </Button>
            </div>
          ))}
        </div>
      ),
    },
    data.setAside.length > 0 && {
      key: 'set-aside',
      label: `${HUB.setAside} — ${HUB.setAsideSummary(toPersianDigits(data.setAside.length))}`,
      children: (
        <div>
          {data.setAside.map((item) => (
            <div className="bk-guide-plain-row" key={item.itemKey}>
              <span className="bk-guide-plain-row__main">
                <span>{item.title}</span>
                <span className="bk-guide-plain-row__meta">
                  <RelativeTime value={item.at} />
                </span>
              </span>
              <Button
                size="small"
                type="link"
                className="bk-guide-link"
                disabledReason={reason}
                onClick={() => onRestore(item.itemKey)}
              >
                {HUB.showAgain}
              </Button>
            </div>
          ))}
        </div>
      ),
    },
  ].filter((item): item is Exclude<typeof item, false> => Boolean(item));
  if (items.length === 0) return null;
  return <Collapse ghost size="small" items={items} />;
}

/* -------------------------------------------------------------------------- */
/* Settings                                                                   */
/* -------------------------------------------------------------------------- */

/** The person's current mode, from their guidance profile (or, until it loads, the quiet reason). */
function currentMode(
  profile: { tipsEnabled: boolean; tipsPausedUntil?: string | null } | undefined,
  data: GuideTipsResponse,
  now: number,
): TipsMode {
  if (profile) {
    if (!profile.tipsEnabled) return 'off';
    const until = profile.tipsPausedUntil ? Date.parse(profile.tipsPausedUntil) : NaN;
    if (Number.isFinite(until) && until > now) {
      return until - now > 36 * 3_600_000 ? 'week' : 'day';
    }
    return 'on';
  }
  if (data.quietReason === 'OFF') return 'off';
  if (data.quietReason === 'PAUSED') {
    const until = data.pausedUntil ? Date.parse(data.pausedUntil) : NaN;
    return Number.isFinite(until) && until - now > 36 * 3_600_000 ? 'week' : 'day';
  }
  return 'on';
}

function TipSettings({
  data,
  readOnly,
}: {
  data: GuideTipsResponse;
  readOnly: boolean;
}): JSX.Element {
  const guidance = useGuidance();
  const profile = guidance.data?.profile;
  const preferences = useUpdateTipPreferences();
  const canViewSettings = useCan(Permission.SETTINGS_VIEW);
  const [now] = useState(() => Date.now());
  const mode = currentMode(profile, data, now);
  const muted = new Set<string>(profile?.mutedTipCategories ?? []);
  const shown = GUIDE_CATEGORIES.filter((category) => !muted.has(category));

  const personal = (
    <div className="bk-guide-hub">
      {readOnly ? (
        <Typography.Text className="bk-muted">{HUB.readOnlyHelper}</Typography.Text>
      ) : null}
      <div>
        <div className="bk-guide-hub__heading" id="bk-guide-mode">
          {HUB.mode}
        </div>
        <Radio.Group
          aria-labelledby="bk-guide-mode"
          value={mode}
          disabled={readOnly || preferences.isPending}
          onChange={(event) => {
            const next = event.target.value as TipsMode;
            preferences.mutate(
              next === 'off'
                ? { tipsEnabled: false, tipsPauseDays: 0 }
                : {
                    tipsEnabled: true,
                    tipsPauseDays: next === 'day' ? 1 : next === 'week' ? 7 : 0,
                  },
            );
          }}
          options={[
            { value: 'on', label: HUB.modeOn },
            { value: 'day', label: HUB.modeDay },
            { value: 'week', label: HUB.modeWeek },
            { value: 'off', label: HUB.modeOff },
          ]}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bk-space-1)' }}
        />
      </div>
      <div>
        <div className="bk-guide-hub__heading" id="bk-guide-categories">
          {HUB.categories}
        </div>
        <Checkbox.Group
          aria-labelledby="bk-guide-categories"
          value={shown}
          disabled={readOnly || preferences.isPending}
          onChange={(checked) => {
            const on = new Set(checked as string[]);
            preferences.mutate({
              mutedTipCategories: GUIDE_CATEGORIES.filter((category) => !on.has(category)),
            });
          }}
          options={GUIDE_CATEGORIES.map((category: GuideCategory) => ({
            value: category,
            label: GUIDE_CATEGORY_LABELS_FA[category],
          }))}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bk-space-1)' }}
        />
      </div>
      {canViewSettings ? <ClinicWideSettings data={data} readOnly={readOnly} /> : null}
    </div>
  );

  return (
    <Collapse size="small" items={[{ key: 'settings', label: HUB.settings, children: personal }]} />
  );
}

function ClinicWideSettings({
  data,
  readOnly,
}: {
  data: GuideTipsResponse;
  readOnly: boolean;
}): JSX.Element {
  const canEdit = useCan(Permission.SETTINGS_EDIT);
  const query = useGuideClinicSettingsQuery(true);
  const update = useUpdateGuideClinicSettings();
  const confirm = useConfirm();
  const settings = query.data;
  const blocked = readOnly ? HUB.readOnlyHelper : !canEdit ? HUB.clinicSwitchReason : null;

  // The rules visible to this person now, once each — hiding one for the
  // clinic hides every instance of it.
  const visibleRules = useMemo(() => {
    const seenRules = new Set<string>();
    const rules: GuideTipResponse[] = [];
    for (const tip of [...data.tips, ...data.later]) {
      if (seenRules.has(tip.ruleKey)) continue;
      seenRules.add(tip.ruleKey);
      rules.push(tip);
    }
    return rules;
  }, [data]);

  if (query.isLoading) return <LoadingBlock rows={2} />;
  if (!settings) return <></>;
  const hiddenKeys = settings.hiddenRules.map((rule) => rule.ruleKey);

  return (
    <section aria-labelledby="bk-guide-clinic-wide">
      <h5 className="bk-guide-hub__heading" id="bk-guide-clinic-wide">
        {HUB.clinicWide}
      </h5>
      <div className="bk-guide-plain-row">
        <span id="bk-guide-clinic-switch">{HUB.clinicSwitch}</span>
        <DisabledReason reason={blocked}>
          <Switch
            checked={settings.enabled}
            loading={update.isPending}
            disabled={Boolean(blocked)}
            aria-labelledby="bk-guide-clinic-switch"
            onChange={(next) => update.mutate({ enabled: next })}
          />
        </DisabledReason>
      </div>

      <div className="bk-guide-hub__heading">{HUB.hiddenRules}</div>
      {settings.hiddenRules.length === 0 ? (
        <Typography.Text className="bk-muted">{HUB.hiddenRulesEmpty}</Typography.Text>
      ) : (
        settings.hiddenRules.map((rule) => (
          <div className="bk-guide-plain-row" key={rule.ruleKey}>
            <span>{rule.titleFa}</span>
            {canEdit ? (
              <Button
                size="small"
                type="link"
                className="bk-guide-link"
                disabledReason={readOnly ? HUB.readOnlyHelper : null}
                onClick={() =>
                  update.mutate({
                    hiddenRules: hiddenKeys.filter((key) => key !== rule.ruleKey) as HiddenRules,
                  })
                }
              >
                {HUB.showAgain}
              </Button>
            ) : null}
          </div>
        ))
      )}

      {canEdit && visibleRules.length > 0 ? (
        <>
          <div className="bk-guide-hub__heading">{HUB.visibleRules}</div>
          {visibleRules.map((tip) => (
            <div className="bk-guide-plain-row" key={tip.ruleKey}>
              <span>{tipTitleText(tip)}</span>
              <Button
                size="small"
                type="link"
                danger
                className="bk-guide-link"
                disabledReason={readOnly ? HUB.readOnlyHelper : null}
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: HUB.hideForAllConfirm,
                      content: HUB.hideForAllContent,
                      okText: HUB.hideForAllOk,
                      cancelText: HUB.cancel,
                    });
                    if (ok) {
                      update.mutate({
                        hiddenRules: [...new Set([...hiddenKeys, tip.ruleKey])] as HiddenRules,
                      });
                    }
                  })();
                }}
              >
                {HUB.hideForAll}
              </Button>
            </div>
          ))}
        </>
      ) : null}
    </section>
  );
}
